/**
 * FusionTestimonialsSlider
 *
 * Custom element wrapping .testimonials-track. Responsibilities:
 * - prev/next scroll by ONE PAGE (stepSize = cardWidth * visibleCount)
 * - dots rendered dynamically per PAGE (nie per card) — ceil(cards / visible)
 * - autoplay with pause on hover/focus/pointerdown
 * - respects prefers-reduced-motion (autoplay off, jump instead of smooth)
 * - keyboard: left/right arrow keys when focus inside the slider
 * - updates prev/next disabled state at track edges
 * - re-measures on resize (visible count changes across breakpoints)
 */

(() => {
  const REDUCED_MOTION = window.matchMedia('(prefers-reduced-motion: reduce)');

  class FusionTestimonialsSlider extends HTMLElement {
    constructor() {
      super();
      this.track = null;
      this.cards = [];
      this.dotsContainer = null;
      this.dots = [];
      this.prevBtn = null;
      this.nextBtn = null;
      this.autoplayTimer = null;
      this.autoplayEnabled = false;
      this.autoplayInterval = 7000;
      this.paused = false;
      this.stepSize = 0;
      this.pageCount = 1;
      this.currentPage = 0;
      this.onScrollDebounced = this.debounce(this.onScroll.bind(this), 80);
      this.onResizeDebounced = this.debounce(this.remeasure.bind(this), 150);
    }

    connectedCallback() {
      this.track = this.querySelector('.testimonials-track');
      if (!this.track) return;

      this.cards = Array.from(this.track.querySelectorAll('.testimonial-card'));
      if (this.cards.length <= 1) return;

      this.dotsContainer = this.querySelector('[data-testimonials-dots]');
      this.prevBtn = this.querySelector('[data-testimonials-prev]');
      this.nextBtn = this.querySelector('[data-testimonials-next]');

      this.autoplayEnabled = this.getAttribute('data-autoplay') === 'true' && !REDUCED_MOTION.matches;
      this.autoplayInterval = Math.max(3000, Number(this.getAttribute('data-interval')) || 7000);

      this.bindEvents();
      this.remeasure();
      if (this.autoplayEnabled) this.startAutoplay();
    }

    disconnectedCallback() {
      this.stopAutoplay();
      window.removeEventListener('resize', this.onResizeDebounced);
    }

    bindEvents() {
      if (this.prevBtn) {
        this.prevBtn.addEventListener('click', () => {
          this.pauseAutoplay(true);
          this.scrollByPages(-1);
        });
      }
      if (this.nextBtn) {
        this.nextBtn.addEventListener('click', () => {
          this.pauseAutoplay(true);
          this.scrollByPages(1);
        });
      }

      // Pause on hover / focus / touch
      this.addEventListener('pointerenter', () => this.pauseAutoplay(false));
      this.addEventListener('pointerleave', () => this.resumeAutoplay());
      this.addEventListener('focusin', () => this.pauseAutoplay(false));
      this.addEventListener('focusout', () => this.resumeAutoplay());
      this.addEventListener('pointerdown', () => this.pauseAutoplay(false), { passive: true });

      // Keyboard nav
      this.addEventListener('keydown', (e) => {
        if (e.key === 'ArrowRight') {
          e.preventDefault();
          this.pauseAutoplay(true);
          this.scrollByPages(1);
        } else if (e.key === 'ArrowLeft') {
          e.preventDefault();
          this.pauseAutoplay(true);
          this.scrollByPages(-1);
        }
      });

      this.track.addEventListener('scroll', this.onScrollDebounced, { passive: true });
      window.addEventListener('resize', this.onResizeDebounced);

      REDUCED_MOTION.addEventListener?.('change', (e) => {
        if (e.matches) this.stopAutoplay();
      });

      document.addEventListener('visibilitychange', () => {
        if (document.hidden) {
          this.stopAutoplay();
        } else if (this.autoplayEnabled && !this.paused) {
          this.startAutoplay();
        }
      });
    }

    /**
     * Measure card width, viewport width, compute visibleCount + pageCount,
     * rebuild dots. Called on init and on window resize.
     */
    remeasure() {
      if (!this.cards.length) return;

      // Card step = offsetLeft delta between two consecutive cards (includes gap if any).
      const firstCard = this.cards[0];
      const secondCard = this.cards[1];
      const cardStep = secondCard
        ? secondCard.offsetLeft - firstCard.offsetLeft
        : firstCard.getBoundingClientRect().width;

      const viewportWidth = this.track.clientWidth;
      const visibleCount = Math.max(1, Math.round(viewportWidth / cardStep));

      this.stepSize = cardStep * visibleCount;
      this.pageCount = Math.max(1, Math.ceil(this.cards.length / visibleCount));

      this.renderDots();
      this.updateActivePage();
      this.updateEdgeState();
    }

    renderDots() {
      if (!this.dotsContainer) return;
      this.dotsContainer.innerHTML = '';
      this.dots = [];

      if (this.pageCount <= 1) {
        this.dotsContainer.setAttribute('hidden', '');
        return;
      }
      this.dotsContainer.removeAttribute('hidden');

      for (let i = 0; i < this.pageCount; i++) {
        const btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'testimonials-dot';
        btn.setAttribute('role', 'tab');
        btn.setAttribute('aria-label', `Strona ${i + 1} z ${this.pageCount}`);
        btn.setAttribute('aria-selected', i === 0 ? 'true' : 'false');
        btn.dataset.testimonialsDot = String(i);
        if (i === 0) btn.classList.add('is-active');
        btn.addEventListener('click', () => {
          this.pauseAutoplay(true);
          this.scrollToPage(i);
        });
        this.dotsContainer.appendChild(btn);
        this.dots.push(btn);
      }
    }

    scrollByPages(delta) {
      const target = Math.max(0, Math.min(this.pageCount - 1, this.currentPage + delta));
      this.scrollToPage(target);
    }

    scrollToPage(pageIdx) {
      const behavior = REDUCED_MOTION.matches ? 'auto' : 'smooth';
      const left = pageIdx * this.stepSize;
      this.track.scrollTo({ left, behavior });
    }

    onScroll() {
      this.updateActivePage();
      this.updateEdgeState();
    }

    updateActivePage() {
      if (!this.stepSize) return;
      const page = Math.round(this.track.scrollLeft / this.stepSize);
      const clamped = Math.max(0, Math.min(this.pageCount - 1, page));
      if (clamped === this.currentPage) return;
      this.currentPage = clamped;
      this.dots.forEach((dot, i) => {
        const active = i === clamped;
        dot.classList.toggle('is-active', active);
        dot.setAttribute('aria-selected', active ? 'true' : 'false');
      });
    }

    updateEdgeState() {
      // No looping — disable buttons at edges to signal boundaries.
      const maxScroll = this.track.scrollWidth - this.track.clientWidth - 1;
      const atStart = this.track.scrollLeft <= 1;
      const atEnd = this.track.scrollLeft >= maxScroll;
      if (this.prevBtn) this.prevBtn.disabled = atStart;
      if (this.nextBtn) this.nextBtn.disabled = atEnd;
    }

    startAutoplay() {
      this.stopAutoplay();
      if (!this.autoplayEnabled) return;
      this.autoplayTimer = window.setInterval(() => {
        const atEnd = this.currentPage >= this.pageCount - 1;
        if (atEnd) {
          this.scrollToPage(0);
        } else {
          this.scrollByPages(1);
        }
      }, this.autoplayInterval);
    }

    stopAutoplay() {
      if (this.autoplayTimer) {
        window.clearInterval(this.autoplayTimer);
        this.autoplayTimer = null;
      }
    }

    pauseAutoplay(hardPause) {
      this.paused = true;
      this.stopAutoplay();
      if (hardPause) this.autoplayEnabled = false;
    }

    resumeAutoplay() {
      this.paused = false;
      if (this.autoplayEnabled) this.startAutoplay();
    }

    debounce(fn, wait) {
      let t;
      return (...args) => {
        window.clearTimeout(t);
        t = window.setTimeout(() => fn(...args), wait);
      };
    }
  }

  if (!customElements.get('fusion-testimonials-slider')) {
    customElements.define('fusion-testimonials-slider', FusionTestimonialsSlider);
  }
})();
